import { withEllipsisMultiline } from "@utils/styles/common-styles";
import DeleteIcon from "@mui/icons-material/Delete";
import { AdmPlan, BaseCompanyIdProps } from "@custom-types/base-types";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useEffect, useMemo, useState } from "react";
import { ReactSetStateFunction } from "@utils/utility-types";
import { formatPlanName } from "@utils/data-display";
import { allPlansSelector } from "@store/plans/plans-selector";
import { Spinner } from "@components/common/spinner";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchPlans, updatePlan } from "@store/plans/plans-thunks";
import { plansStateSelector } from "@store/plans/plans-slice";
import {
  formatCurrentAndMaxValueLimit,
  UNLIMITED,
} from "@utils/limit-data-display";
import {
  Dialog,
  Tooltip,
  useToast,
  Text,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@flat-ui";

interface Props extends BaseCompanyIdProps {
  /** Setter for shouldDisableContinueButton */
  setShouldDisableContinueButton: ReactSetStateFunction<boolean>;
}

/** Content for step 2 of delete workspace */
export function DeleteWorkspaceStep2({
  companyId,
  setShouldDisableContinueButton,
}: Props): React.JSX.Element {
  const [selectedPlan, setSelectedPlan] = useState<AdmPlan>();
  const [shouldShowDeactivateAllDialog, setShouldShowDeactivateAllDialog] =
    useState<boolean>(false);
  const [isDeactivatingAllPlans, setIsDeactivatingAllPlans] =
    useState<boolean>(false);
  const { handleErrorWithToast } = useErrorHandler();
  const dispatch = useAppDispatch();
  const { openToast } = useToast();

  const plans = useAppSelector(allPlansSelector({ shouldShowAllPlans: true }));
  const { fetchingPlan } = useAppSelector(plansStateSelector);

  /** All the plans that needs to be deactivated before deleting the workspace */
  const filterPlans = useMemo(() => {
    return plans.filter(
      ({ status }) =>
        !(
          status.identifier === "deactivated" ||
          status.identifier === "inactive"
        )
    );
  }, [plans]);

  // Fetches the plans related to the subject entity when the component mounts or when the subjectType/subjectId changes
  useEffect(() => {
    async function fetchingPlans(): Promise<void> {
      try {
        await dispatch(
          fetchPlans({ subjectType: "COMPANY", subjectId: companyId })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `PlanInfo-${Date.now().toString()}`,
          title: "Could not load plans of COMPANY",
          error,
        });
      }
    }

    void fetchingPlans();
  }, [companyId, dispatch, handleErrorWithToast]);

  function deactivatePlan(planId: string): Promise<AdmPlan> {
    return dispatch(
      updatePlan({
        planId,
        updatedProps: {
          type: "deactivating",
          deactivatedComment:
            "Deactivated from workspace to allow workspace deletion",
        },
      })
    ).unwrap();
  }

  async function onDeactivatePlanFromCompany(plan: AdmPlan): Promise<void> {
    try {
      await deactivatePlan(plan.id);

      openToast({
        title: "Plan successfully deactivated from workspace.",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `deactivate-plan-from-workspace-${Date.now().toString()}`,
        title: "Could not deactivate plan from workspace",
        error,
      });
    } finally {
      setSelectedPlan(undefined);
    }
  }

  async function onDeactivateAllPlansFromCompany(): Promise<void> {
    setIsDeactivatingAllPlans(true);
    try {
      await Promise.all(filterPlans.map((plan) => deactivatePlan(plan.id)));

      openToast({
        title: "All plans successfully deactivated from workspace.",
      });
    } catch (error) {
      handleErrorWithToast({
        id: `deactivate-all-plans-from-workspace-${Date.now().toString()}`,
        title: "Could not deactivate all plans from workspace",
        error,
      });
    } finally {
      setIsDeactivatingAllPlans(false);
      setShouldShowDeactivateAllDialog(false);
    }
  }

  useEffect(() => {
    if (fetchingPlan.allPlans === "pending") {
      setShouldDisableContinueButton(true);
      return;
    }

    setShouldDisableContinueButton(filterPlans.length > 0);
  }, [
    fetchingPlan.allPlans,
    filterPlans.length,
    setShouldDisableContinueButton,
  ]);

  if (fetchingPlan.allPlans === "pending") {
    return <Spinner />;
  }

  return (
    <Text variant="bodyL">
      Before deleting this workspace, all the following plans must be
      deactivated from it:
      {filterPlans.length === 0 ? (
        <Text variant="bodyL" sx={{ display: "block", mt: 2 }}>
          This workspace has no plans. You can safely move to the next step.
        </Text>
      ) : (
        <>
          <br />
          <Button
            variant="secondary"
            onClick={() => setShouldShowDeactivateAllDialog(true)}
            sx={{ my: 1 }}
          >
            Deactivate all plans at once
          </Button>
          <List dense={false} sx={{ maxHeight: 300, overflow: "auto" }}>
            {filterPlans.map((plan) => (
              <ListItem
                key={plan.id}
                sx={{
                  px: "0px",

                  "& .MuiListItemText-root": {
                    ml: "12px",
                    ...withEllipsisMultiline,
                    maxWidth: "350px",
                  },
                }}
                secondaryAction={
                  <IconButton
                    onClick={() => {
                      setSelectedPlan(plan);
                    }}
                  >
                    <Tooltip title="Deactivate plan from workspace">
                      <DeleteIcon fontSize="small" />
                    </Tooltip>
                  </IconButton>
                }
              >
                <ListItemText
                  primary={formatPlanName(plan)}
                  secondary={
                    plan.constraints
                      .map(formatCurrentAndMaxValueLimit)
                      .join(", ") || UNLIMITED
                  }
                />
              </ListItem>
            ))}
          </List>

          <Dialog
            open={!!selectedPlan}
            title="Confirm plan deactivation"
            onCancel={() => setSelectedPlan(undefined)}
            onConfirm={() =>
              selectedPlan && onDeactivatePlanFromCompany(selectedPlan)
            }
            showSpinner={fetchingPlan.updatingPlan === "pending"}
          >
            <Text variant="bodyL">
              Are you sure you want to deactivate this plan from workspace?
            </Text>
          </Dialog>

          <Dialog
            open={shouldShowDeactivateAllDialog}
            title="Confirm deactivating all plans"
            onCancel={() => setShouldShowDeactivateAllDialog(false)}
            onConfirm={onDeactivateAllPlansFromCompany}
            showSpinner={isDeactivatingAllPlans}
          >
            <Text variant="bodyL">
              Are you sure you want to deactivate all {filterPlans.length} plans
              from this workspace?
            </Text>
          </Dialog>
        </>
      )}
    </Text>
  );
}
